/**
 * NotificationBell — bell icon with unread count badge and dropdown panel.
 *
 * Displays the count of unread notifications as a red badge on a bell icon.
 * Clicking the bell opens a dropdown panel showing the 20 most recent
 * notifications. Each notification can be clicked to mark it read and
 * navigate to its linked entity (if linkUrl is set).
 *
 * Phase 20: end-to-end notification delivery — lifecycle events, CD maturity
 * alerts, DQ alerts, and staff review queue items surface here in real time.
 *
 * Usage:
 *   <NotificationBell /> — drop into the top navigation bar
 *
 * Behaviour:
 *   - Unread count badge hidden when count is 0
 *   - Badge shows "99+" for counts above 99
 *   - "Mark all read" button appears when unread > 0
 *   - Clicking outside the panel closes it
 *   - Clicking a notification marks it read + navigates to linkUrl if present
 */
import { useState, useRef, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { useNotifications, NotificationItem } from '../../hooks/useNotifications';

export function NotificationBell() {
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button with unread count badge */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-sunken transition-colors"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Bell className="h-5 w-5 text-ink-soft" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-fail text-[10px] font-bold text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification dropdown panel */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-96 rounded-lg border border-line bg-white shadow-lg z-50">
          {/* Panel header */}
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <h3 className="text-sm font-semibold text-ink">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={() => void markAllRead()}
                className="text-xs text-info hover:text-info"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notification list */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-ink-muted">
                No notifications yet
              </div>
            ) : (
              notifications.slice(0, 20).map((n) => (
                <NotificationRow key={n.id} notification={n} onMarkRead={markRead} />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * NotificationRow — single notification item in the dropdown panel.
 *
 * Shows an unread indicator dot for unread items (readAt is null).
 * Clicking marks the item read and navigates to linkUrl if provided.
 */
function NotificationRow({
  notification,
  onMarkRead,
}: {
  notification: NotificationItem;
  onMarkRead: (id: string) => Promise<void>;
}) {
  const isUnread = !notification.readAt;

  const handleClick = () => {
    if (isUnread) void onMarkRead(notification.id);
    if (notification.linkUrl) window.location.href = notification.linkUrl;
  };

  return (
    <button
      onClick={handleClick}
      className={`w-full px-4 py-3 text-left hover:bg-bg border-b border-sunken last:border-0 ${
        isUnread ? 'bg-info-soft/50' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Unread indicator dot */}
        {isUnread && (
          <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-brand" />
        )}
        <div className={isUnread ? '' : 'ml-5'}>
          <p className="text-sm text-ink">{notification.message}</p>
          <p className="mt-1 text-xs text-ink-muted">
            {new Date(notification.createdAt).toLocaleString()}
          </p>
        </div>
      </div>
    </button>
  );
}
